import { createMemo, createRoot, createSignal } from 'solid-js';
import { geolocation } from '~/lib/geolocation';
import { haversineMeters } from '~/lib/geo';
import { type GameNode, nodeStore } from '~/lib/nodes';

/** Engagement range in metres.
 *
 *  Tuning value, kept in one place. 40 m was the original Ingress-derived
 *  figure; 200 m (≈ two city blocks) is the current playtest value after a
 *  real Trinidad walk felt too tight at 40. Variable range — by node tier,
 *  equipment, or action type — is a future game-design consideration. */
export const ENGAGEMENT_RANGE_M = 200;

/** Shared, reactive node-view state derived from the node store and the live
 *  geolocation singleton. MAP and JOBS both consume this so the title card,
 *  range gate, engagement ring, in-range highlight, and JOBS sort are all
 *  driven by one distance computation and never disagree.
 *
 *  Module-level singleton (createRoot) like the other lib singletons, so the
 *  selection survives MAP/JOBS tab remounts within a page load. */
function createNodeView() {
  // The node the user explicitly tapped to preview; null = follow nearest.
  const [selectedId, setSelectedId] = createSignal<string | null>(null);

  /** id -> metres from the live player position. null before the first fix
   *  (no player position yet). One pass per position update. */
  const distances = createMemo<Map<string, number> | null>(() => {
    const fix = geolocation.latest();
    const nodes = nodeStore.nodes();
    if (!fix) return null;
    const out = new Map<string, number>();
    for (const n of nodes) out.set(n.id, haversineMeters(fix.lat, fix.lng, n.lat, n.lng));
    return out;
  });

  const distanceOf = (id: string): number | null => distances()?.get(id) ?? null;

  /** Within engagement range of the player. False before the first fix. */
  const isInRange = (id: string): boolean => {
    const d = distanceOf(id);
    return d != null && d <= ENGAGEMENT_RANGE_M;
  };

  /** Hackable roles: pure targets and dual nodes (a dual node is a shop/service
   *  that can also be hacked, at low tier). Pure shops/services are never
   *  hackable. */
  const isHackable = (node: GameNode): boolean => node.role === 'target' || node.role === 'dual';

  /** A node can be engaged only when it is hackable and in range. */
  const isEngageable = (node: GameNode): boolean => isHackable(node) && isInRange(node.id);

  /** Nearest hackable node (target or dual) to the player; null if none loaded.
   *  Drives the card's ENGAGE default (any node can still be tap-previewed).
   *  With no fix yet every distance is Infinity, so the first hackable node
   *  (stable order) wins, shown without one. */
  const nearestTarget = createMemo<GameNode | null>(() => {
    const dist = distances();
    let best: GameNode | null = null;
    let bestD = Infinity;
    for (const n of nodeStore.nodes()) {
      if (!isHackable(n)) continue;
      const d = dist?.get(n.id) ?? Infinity;
      if (best === null || d < bestD) {
        best = n;
        bestD = d;
      }
    }
    return best;
  });

  /** The explicitly-selected node (any role), or null if cleared or absent. */
  const selected = createMemo<GameNode | null>(() => {
    const id = selectedId();
    return id === null ? null : (nodeStore.nodes().find((n) => n.id === id) ?? null);
  });

  /** The node the title card shows: explicit selection (any role), else the
   *  nearest hackable node. */
  const featured = createMemo<GameNode | null>(() => selected() ?? nearestTarget());

  /** Hackable nodes (the JOBS-board jobs: targets + dual), nearest-first.
   *  Pre-fix they retain stable store order rather than NaN-sorting. */
  const targetsByDistance = createMemo<GameNode[]>(() => {
    const dist = distances();
    const targets = nodeStore.nodes().filter(isHackable);
    if (!dist) return targets;
    return [...targets].sort(
      (a, b) => (dist.get(a.id) ?? Infinity) - (dist.get(b.id) ?? Infinity),
    );
  });

  return {
    distances,
    distanceOf,
    isInRange,
    isHackable,
    isEngageable,
    nearestTarget,
    selected,
    featured,
    targetsByDistance,
    selectedId,
    select: (id: string) => setSelectedId(id),
    clearSelection: () => setSelectedId(null),
  };
}

export const nodeView = createRoot(() => createNodeView());
